/** Display formatting. Money is integer fils (1 AED = 100 fils) everywhere until shown. */
import type { Language } from './i18n.ts';

const LOCALES: Record<Language, string> = { ar: 'ar-AE-u-nu-latn', en: 'en-AE' };

export function formatAed(fils: number, language: Language): string {
  const amount = (fils / 100).toLocaleString(LOCALES[language], {
    minimumFractionDigits: fils % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return language === 'ar' ? `${amount} درهم` : `AED ${amount}`;
}

export function formatCount(value: number, language: Language): string {
  return Math.round(value).toLocaleString(LOCALES[language]);
}

export function formatPercent(share: number, language: Language): string {
  return share.toLocaleString(LOCALES[language], { style: 'percent', maximumFractionDigits: 0 });
}

/** A change as a percent with its sign ("+25%", "−30%", "0%"), in the app language's style. */
export function formatChange(share: number, language: Language): string {
  return share.toLocaleString(LOCALES[language], {
    style: 'percent',
    maximumFractionDigits: 0,
    signDisplay: 'exceptZero',
  });
}

export function formatUsd(value: number, language: Language): string {
  return value.toLocaleString(LOCALES[language], { style: 'currency', currency: 'USD' });
}

/** Date and time on the business clock. */
export function formatDateTime(iso: string, language: Language): string {
  return new Date(iso).toLocaleString(LOCALES[language], {
    timeZone: 'Asia/Dubai',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
}

/** "12 minutes ago" style, for the system health page. */
export function formatAgo(iso: string, now: Date, language: Language): string {
  const minutes = Math.round((now.getTime() - new Date(iso).getTime()) / 60_000);
  const rtf = new Intl.RelativeTimeFormat(LOCALES[language], { numeric: 'auto' });
  if (Math.abs(minutes) < 60) return rtf.format(-minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 48) return rtf.format(-hours, 'hour');
  return rtf.format(-Math.round(hours / 24), 'day');
}

/** AED text field → fils, or null if it is not a positive amount with at most 2 decimals. */
export function parseAedInput(text: string): number | null {
  const trimmed = text.trim().replace(/,/g, '');
  if (!/^\d{1,9}(\.\d{1,2})?$/.test(trimmed)) return null;
  const [whole = '0', fraction = ''] = trimmed.split('.');
  const value = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return value > 0 ? value : null;
}

/** Whole percent from 0 to `max` (stage 2 budget move limit), or null. */
export function parsePercentInput(text: string, max = 30): number | null {
  const trimmed = text.trim();
  if (!/^\d{1,3}$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value <= max ? value : null;
}
